// API客户端服务
export const API_BASE_URL =
  (import.meta.env.VITE_API_URL || 'http://localhost:3001') + '/api';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    limit: number;
    offset: number;
    total: number;
  };
  error?: string;
}

export interface RatedBrand extends Brand {
  overallScore?: number;
  ratingUpdatedAt?: string;
}

interface Brand {
  id: number;
  name: string;
  description: string;
  founded: string;
  headquarters: string;
  website: string;
  logo: string | null;
  categories: string[];
  socialMedia: {
    instagram?: string;
    twitter?: string;
  };
  parent_group?: string;
  ownership?: string;
  hard_vs_soft?: string;
  region_headquarters?: string;
  createdAt: string;
  updatedAt: string;
}

interface BrandRating {
  id: number;
  brandId: number;
  overallScore: number;
  dimensions: {
    environmental: {
      score: number;
      confidence: number;
      evidence: string[];
      sources: string[];
      lastVerified: string;
    };
    laborRights: {
      score: number;
      confidence: number;
      evidence: string[];
      sources: string[];
      lastVerified: string;
    };
    animalWelfare: {
      score: number;
      confidence: number;
      evidence: string[];
      sources: string[];
      lastVerified: string;
    };
    transparency: {
      score: number;
      confidence: number;
      evidence: string[];
      sources: string[];
      lastVerified: string;
    };
    socialResponsibility: {
      score: number;
      confidence: number;
      evidence: string[];
      sources: string[];
      lastVerified: string;
    };
    carbonFootprint: {
      score: number;
      confidence: number;
      evidence: string[];
      sources: string[];
      lastVerified: string;
    };
  };
  dataSources: string[];
  confidence: number;
  createdAt: string;
  updatedAt: string;
}

interface User {
  id: number;
  email: string;
  username?: string;
  preferences: {
    notifications: {
      email: boolean;
      push: boolean;
      newRecommendations: boolean;
      ratingUpdates: boolean;
      communityUpdates: boolean;
    };
    language: string;
    currency: string;
    categories: string[];
    privacySettings: {
      profileVisible: boolean;
      reviewsVisible: boolean;
      activityVisible: boolean;
    };
  };
  created_at: string;
  updated_at: string;
  last_active: string;
}

interface AuthResponse {
  user: User;
  token: string;
}

class ApiClient {
  private token: string | null = null;

  constructor() {
    // 从localStorage获取token
    this.token = localStorage.getItem('auth_token');
  }

  // 聚合评分相关API
  async getTopRatedBrands(limit?: number): Promise<ApiResponse<RatedBrand[]>> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request<ApiResponse<RatedBrand[]>>(
      `/brands/top-rated${params}`
    );
  }

  async getTrendingRatedBrands(
    limit?: number,
    windowDays?: number
  ): Promise<ApiResponse<RatedBrand[]>> {
    const sp = new URLSearchParams();
    if (limit) sp.append('limit', String(limit));
    if (windowDays) sp.append('windowDays', String(windowDays));
    const query = sp.toString();
    return this.request<ApiResponse<RatedBrand[]>>(
      `/brands/trending${query ? `?${query}` : ''}`
    );
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  private async request<T>(
    endpoint: string,
    options?: Omit<RequestInit, 'headers'> & {
      headers?: Record<string, string>;
    }
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers || {}),
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers,
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'API request failed');
    }

    return data;
  }

  // 认证相关API
  async register(
    email: string,
    password: string,
    username?: string
  ): Promise<ApiResponse<AuthResponse>> {
    return this.request<ApiResponse<AuthResponse>>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, username }),
    });
  }

  async login(
    email: string,
    password: string
  ): Promise<ApiResponse<AuthResponse>> {
    return this.request<ApiResponse<AuthResponse>>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async getProfile(): Promise<ApiResponse<User>> {
    return this.request<ApiResponse<User>>('/auth/profile');
  }

  async updatePreferences(
    preferences: Record<string, unknown>
  ): Promise<ApiResponse<User>> {
    return this.request<ApiResponse<User>>('/auth/preferences', {
      method: 'PUT',
      body: JSON.stringify({ preferences }),
    });
  }

  // 浏览历史相关API
  async recordVisit(
    brandId: number,
    sessionId?: string,
    duration?: number
  ): Promise<ApiResponse<unknown>> {
    return this.request<ApiResponse<unknown>>(`/history/visit/${brandId}`, {
      method: 'POST',
      body: JSON.stringify({ sessionId, duration }),
    });
  }

  async getBrowsingHistory(limit?: number): Promise<ApiResponse<Brand[]>> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request<ApiResponse<Brand[]>>(`/history${params}`);
  }

  async getRecentlyViewed(limit?: number): Promise<ApiResponse<Brand[]>> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request<ApiResponse<Brand[]>>(`/history/recent${params}`);
  }

  async clearBrowsingHistory(): Promise<ApiResponse<unknown>> {
    return this.request<ApiResponse<unknown>>('/history', {
      method: 'DELETE',
    });
  }

  // 收藏相关API
  async addFavorite(brandId: number): Promise<ApiResponse<unknown>> {
    return this.request<ApiResponse<unknown>>(`/favorites/${brandId}`, {
      method: 'POST',
    });
  }

  async removeFavorite(brandId: number): Promise<ApiResponse<unknown>> {
    return this.request<ApiResponse<unknown>>(`/favorites/${brandId}`, {
      method: 'DELETE',
    });
  }

  async getFavorites(): Promise<ApiResponse<Brand[]>> {
    return this.request<ApiResponse<Brand[]>>('/favorites');
  }

  async checkFavorite(
    brandId: number
  ): Promise<ApiResponse<{ isFavorite: boolean }>> {
    return this.request<ApiResponse<{ isFavorite: boolean }>>(
      `/favorites/${brandId}/check`
    );
  }

  async getFavoriteStats(): Promise<
    ApiResponse<{ total: number; categories: Record<string, number> }>
  > {
    return this.request<
      ApiResponse<{ total: number; categories: Record<string, number> }>
    >('/favorites/stats');
  }

  async getFavoriteIds(): Promise<ApiResponse<number[]>> {
    return this.request<ApiResponse<number[]>>('/favorites/ids');
  }

  // 推荐相关API
  async getPersonalRecommendations(
    limit?: number
  ): Promise<ApiResponse<Brand[]>> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request<ApiResponse<Brand[]>>(
      `/recommendations/personal${params}`
    );
  }

  async getTrendingBrands(limit?: number): Promise<ApiResponse<Brand[]>> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request<ApiResponse<Brand[]>>(
      `/recommendations/trending${params}`
    );
  }

  async getSimilarBrands(
    brandId: number,
    limit?: number
  ): Promise<ApiResponse<Brand[]>> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request<ApiResponse<Brand[]>>(
      `/recommendations/similar/${brandId}${params}`
    );
  }

  // 品牌相关API
  async getBrands(params?: {
    search?: string;
    category?: string;
    startsWith?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<Brand[]>> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append('search', params.search);
    if (params?.category) searchParams.append('category', params.category);
    if (params?.startsWith)
      searchParams.append('startsWith', params.startsWith);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const query = searchParams.toString();
    return this.request<ApiResponse<Brand[]>>(
      `/brands${query ? `?${query}` : ''}`
    );
  }

  async getBrand(id: number): Promise<ApiResponse<Brand>> {
    return this.request<ApiResponse<Brand>>(`/brands/${id}`);
  }

  async getBrandRating(brandId: number): Promise<ApiResponse<BrandRating>> {
    return this.request<ApiResponse<BrandRating>>(`/ratings/brand/${brandId}`);
  }

  async getTopBrands(): Promise<ApiResponse<Brand[]>> {
    return this.request<ApiResponse<Brand[]>>('/ratings/top');
  }

  // 通用GET方法
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint);
  }

  // 通用POST方法
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // 通用PUT方法
  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // 通用DELETE方法
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }
}

export const apiClient = new ApiClient();
export type { Brand, BrandRating, ApiResponse, User, AuthResponse };
